import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CheckCircle, ListChecks } from "@phosphor-icons/react";

const STATUS_STYLE = {
  draft: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
  in_review: "border-[var(--vs-info)]/40 text-[var(--vs-info)] bg-[var(--vs-info)]/10",
  published: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
};

const DEFAULT_SUBJECTS = ["Math", "Science", "English", "Hindi", "Social Studies"];

export default function Marksheets() {
  const { user } = useAuth();
  const [marksheets, setMarksheets] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", exam_name: "Annual 2026", academic_year: "2026" });

  const [enterOpen, setEnterOpen] = useState(false);
  const [enterTarget, setEnterTarget] = useState(null);
  const [marks, setMarks] = useState({}); // subject -> obtained

  // Bulk publish state
  const [schools, setSchools] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ school_id: "", class_name: "", exam_name: "Annual 2026", academic_year: String(new Date().getFullYear()) });

  const refresh = () => api.get("/school/marksheets").then((r) => setMarksheets(r.data));

  useEffect(() => {
    refresh();
    api.get("/students").then((r) => setStudents(r.data));
    api.get("/admin/schools").then((r) => {
      setSchools(r.data || []);
      if ((r.data || []).length && !bulkForm.school_id) {
        setBulkForm((f) => ({ ...f, school_id: r.data[0].id }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const subjects = DEFAULT_SUBJECTS.map((s) => ({ subject: s, max: 100, obtained: 0 }));
      await api.post("/school/marksheets", { ...form, subjects });
      toast.success("Marksheet draft created");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const submitMarks = async () => {
    try {
      // Teacher: only submit own subject. Admin: submit all in dialog.
      if (user?.role === "teacher") {
        const subj = user.subject;
        await api.post(`/school/marksheets/${enterTarget.id}/marks`, { subject: subj, obtained: Number(marks[subj] || 0) });
      } else {
        for (const s of enterTarget.subjects || []) {
          if (marks[s.subject] !== undefined) {
            await api.post(`/school/marksheets/${enterTarget.id}/marks`, { subject: s.subject, obtained: Number(marks[s.subject] || 0) });
          }
        }
      }
      toast.success("Marks submitted");
      setEnterOpen(false);
      setMarks({});
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const publish = async (ms) => {
    try {
      await api.post(`/school/marksheets/${ms.id}/publish`);
      toast.success(`Published — visible to donor`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const bulkPublish = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/school/marksheets/publish-class", bulkForm);
      toast.success(`Published ${res.data.published} marksheet${res.data.published === 1 ? "" : "s"}`);
      setBulkOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const bulkPreviewCount = marksheets.filter(
    (m) => m.status === "in_review"
      && (!bulkForm.school_id || m.school_id === bulkForm.school_id)
      && (!bulkForm.class_name || (m.class_name || "") === bulkForm.class_name)
      && (!bulkForm.exam_name || m.exam_name === bulkForm.exam_name)
      && (!bulkForm.academic_year || m.academic_year === bulkForm.academic_year)
  ).length;

  const canCreate = ["super_admin", "school_admin"].includes(user?.role);

  return (
    <DashboardLayout title="Marksheets" subtitle="Teacher mark entry → admin review → publish to donor.">
      {canCreate && (
        <div className="flex justify-end gap-2 mb-4">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button data-testid="bulk-publish-btn" variant="outline">
                <ListChecks size={14} className="mr-1.5" /> Bulk publish class
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk publish marksheets for a class</DialogTitle>
                <DialogDescription>Move every in-review marksheet that matches the filter into the published state. Donors of sponsored students see the result immediately.</DialogDescription>
              </DialogHeader>
              <form onSubmit={bulkPublish} className="space-y-3">
                <div>
                  <Label>School</Label>
                  <Select value={bulkForm.school_id} onValueChange={(v) => setBulkForm({ ...bulkForm, school_id: v })}>
                    <SelectTrigger data-testid="bulk-school"><SelectValue placeholder="Choose school" /></SelectTrigger>
                    <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Class</Label><Input data-testid="bulk-class" required value={bulkForm.class_name} onChange={(e) => setBulkForm({ ...bulkForm, class_name: e.target.value })} placeholder="e.g. 8" /></div>
                  <div><Label>Exam</Label><Input data-testid="bulk-exam" required value={bulkForm.exam_name} onChange={(e) => setBulkForm({ ...bulkForm, exam_name: e.target.value })} /></div>
                  <div><Label>Academic year</Label><Input data-testid="bulk-year" required value={bulkForm.academic_year} onChange={(e) => setBulkForm({ ...bulkForm, academic_year: e.target.value })} /></div>
                </div>
                <div className="text-xs p-3 rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]">
                  <strong>{bulkPreviewCount}</strong> in-review marksheet{bulkPreviewCount === 1 ? "" : "s"} match the filter and will be published.
                  Donors of sponsored students will see results immediately.
                </div>
                <Button data-testid="confirm-bulk-publish" type="submit" disabled={bulkPreviewCount === 0} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">
                  <CheckCircle size={14} className="mr-1.5" /> Publish {bulkPreviewCount} marksheet{bulkPreviewCount === 1 ? "" : "s"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-marksheet-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
                <Plus size={14} className="mr-1.5" /> New marksheet
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create marksheet</DialogTitle>
                <DialogDescription>Start a draft marksheet for a single student and exam. Subjects use the default 5-subject template.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>Student</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Choose student" /></SelectTrigger>
                    <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.vs_id} — {s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Exam</Label><Input required value={form.exam_name} onChange={(e) => setForm({ ...form, exam_name: e.target.value })} /></div>
                  <div><Label>Academic Year</Label><Input required value={form.academic_year} onChange={(e) => setForm({ ...form, academic_year: e.target.value })} /></div>
                </div>
                <p className="text-xs text-[var(--vs-text-secondary)]">Subjects: {DEFAULT_SUBJECTS.join(", ")} (max 100 each)</p>
                <Button type="submit" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Create draft</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">VS-ID</th><th className="px-4 py-3">Student</th>
            <th className="px-4 py-3">Class · Roll</th>
            <th className="px-4 py-3">Exam</th>
            <th className="px-4 py-3">Year</th><th className="px-4 py-3">Total</th>
            <th className="px-4 py-3">%</th><th className="px-4 py-3">Grade</th>
            <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {marksheets.map((m) => (
              <tr key={m.id} data-testid={`marksheet-row-${m.id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{m.student_vs_id}</td>
                <td className="px-4 py-3 font-medium">{m.student_name || "—"}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="font-medium">Class {m.class_name || "—"}{m.section ? `-${m.section}` : ""}</span>
                  {m.roll_number && <span className="ml-2 font-mono text-[var(--vs-text-secondary)]">Roll {m.roll_number}</span>}
                </td>
                <td className="px-4 py-3">{m.exam_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{m.academic_year}</td>
                <td className="px-4 py-3 font-mono">{m.total_obtained}/{m.total_max}</td>
                <td className="px-4 py-3 font-mono">{m.percentage}%</td>
                <td className="px-4 py-3 font-display font-semibold">{m.grade || "—"}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={STATUS_STYLE[m.status]}>{m.status.replace("_", " ")}</Badge></td>
                <td className="px-4 py-3 flex gap-2">
                  {(user?.role !== "donor") && (
                    <Button size="sm" variant="outline" data-testid={`enter-marks-${m.id}`} onClick={() => { setEnterTarget(m); setMarks({}); setEnterOpen(true); }}>
                      Enter marks
                    </Button>
                  )}
                  {canCreate && m.status !== "published" && (
                    <Button size="sm" data-testid={`publish-${m.id}`} onClick={() => publish(m)} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
                      <CheckCircle size={12} className="mr-1.5" />Publish
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {marksheets.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No marksheets yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Enter marks — {enterTarget?.exam_name}
              {enterTarget && (
                <div className="text-xs font-normal text-[var(--vs-text-secondary)] mt-1">
                  {enterTarget.student_name} · Class {enterTarget.class_name}{enterTarget.section ? `-${enterTarget.section}` : ""}
                  {enterTarget.roll_number ? ` · Roll ${enterTarget.roll_number}` : ""}
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          {enterTarget && (
            <div className="space-y-3">
              {(enterTarget.subjects || []).map((s) => {
                const isOwn = user?.role !== "teacher" || (user.subject || "").toLowerCase() === s.subject.toLowerCase();
                return (
                  <div key={s.subject} className="grid grid-cols-3 items-center gap-3">
                    <div className="text-sm font-medium">{s.subject} <span className="text-xs text-[var(--vs-text-secondary)]">/{s.max}</span></div>
                    <Input
                      data-testid={`mark-input-${s.subject.toLowerCase()}`}
                      type="number" min={0} max={s.max}
                      disabled={!isOwn}
                      defaultValue={s.obtained}
                      onChange={(e) => setMarks((m) => ({ ...m, [s.subject]: e.target.value }))}
                    />
                    {!isOwn && <span className="text-[10px] font-mono text-[var(--vs-text-secondary)]">Locked — not your subject</span>}
                  </div>
                );
              })}
              <Button onClick={submitMarks} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] w-full">Submit marks</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
